// ===========================================
// Hybrid Booking React Query Hooks
// ===========================================
// Custom hooks for hybrid booking API operations

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from 'notistack';
import {
  hybridBookingApi,
  calendarApi,
  BookingFilters,
  CreateBookingInput,
  RescheduleBookingInput,
  ChangeBookingRoomInput,
  ChangeBookingTeacherInput,
  LessonBookingFilters,
  CalendarEventsFilters,
  AdminPreBookingInput,
  AdminPreBookingFilters,
  BookingOpenStats,
  TermBookingOpenStats,
  TeacherTaskSessionsParams,
  SendRemindersResult,
} from '../api/hybridBooking.api';
import { lessonKeys } from './useLessons';
import { occurrenceDateForWeek } from '../utils/dateUtils';
import type {
  InstrumentOption,
  ProgramLevelOption,
  TermBookingOpenStatsLesson,
  TermOption,
} from '../api/hybridBooking.api';

// ===========================================
// QUERY KEYS
// ===========================================

export const hybridBookingKeys = {
  all: ['hybrid-bookings'] as const,
  availableSlots: (lessonId: string, weekNumber: number) =>
    [...hybridBookingKeys.all, 'slots', lessonId, weekNumber] as const,
  myBookings: (filters?: BookingFilters) =>
    [...hybridBookingKeys.all, 'my', filters] as const,
  booking: (id: string) => [...hybridBookingKeys.all, 'detail', id] as const,
  lessonBookings: (lessonId: string, filters?: LessonBookingFilters) =>
    [...hybridBookingKeys.all, 'lesson', lessonId, filters] as const,
  stats: (lessonId: string, weekNumber?: number) =>
    [...hybridBookingKeys.all, 'stats', lessonId, weekNumber] as const,
  unbookedStudents: (lessonId: string, weekNumber: number) =>
    [...hybridBookingKeys.all, 'unbooked', lessonId, weekNumber] as const,
  adminAvailableSlots: (
    lessonId: string,
    weekNumber: number,
    bookingId?: string,
    teacherId?: string
  ) =>
    [...hybridBookingKeys.all, 'admin-slots', lessonId, weekNumber, bookingId, teacherId] as const,
  eligibleClinicTeachers: (lessonId: string) =>
    [...hybridBookingKeys.all, 'eligible-teachers', lessonId] as const,
  adminPreBookings: (filters?: AdminPreBookingFilters) =>
    [...hybridBookingKeys.all, 'admin-pre-bookings', filters] as const,
  adminStudentBookings: (studentId: string, includeCancelled?: boolean) =>
    [...hybridBookingKeys.all, 'admin-student', studentId, includeCancelled ?? false] as const,
  teacherTasks: (params: TeacherTaskSessionsParams | 'disabled') =>
    [...hybridBookingKeys.all, 'teacher-tasks', params] as const,
  openWeekSlots: (bookingId: string) =>
    [...hybridBookingKeys.all, 'open-week-slots', bookingId] as const,
};

export const calendarKeys = {
  all: ['calendar'] as const,
  events: (filters?: CalendarEventsFilters) =>
    [...calendarKeys.all, 'events', filters] as const,
  myEvents: (filters?: CalendarEventsFilters) =>
    [...calendarKeys.all, 'my-events', filters] as const,
};

// ===========================================
// PARENT HYBRID LESSON ACCESS
// ===========================================

/**
 * Get hybrid lessons the parent's children are enrolled in
 */
export function useMyHybridLessons() {
  return useQuery({
    queryKey: [...hybridBookingKeys.all, 'my-lessons'] as const,
    queryFn: () => hybridBookingApi.getMyHybridLessons(),
  });
}

// ===========================================
// PARENT BOOKING QUERIES
// ===========================================

/**
 * Get available time slots for a specific week
 */
export function useAvailableSlots(lessonId: string, weekNumber: number) {
  return useQuery({
    queryKey: hybridBookingKeys.availableSlots(lessonId, weekNumber),
    queryFn: () => hybridBookingApi.getAvailableSlots(lessonId, weekNumber),
    enabled: !!lessonId && weekNumber > 0,
  });
}

/**
 * Slots across ALL remaining term weeks for an OPEN-WEEK booking (the admin
 * freed this clinic from its fixed week). Only fetched when the booking
 * actually carries the flag — pass `enabled` accordingly.
 */
export function useOpenWeekSlots(bookingId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: hybridBookingKeys.openWeekSlots(bookingId || ''),
    queryFn: () => hybridBookingApi.getOpenWeekSlots(bookingId!),
    enabled: enabled && !!bookingId,
  });
}

/**
 * Get parent's own bookings
 */
export function useMyBookings(filters?: BookingFilters) {
  return useQuery({
    queryKey: hybridBookingKeys.myBookings(filters),
    queryFn: () => hybridBookingApi.getMyBookings(filters),
  });
}

/**
 * Get a single booking by ID
 */
export function useBooking(id: string) {
  return useQuery({
    queryKey: hybridBookingKeys.booking(id),
    queryFn: () => hybridBookingApi.getById(id),
    enabled: !!id,
  });
}

// ===========================================
// PARENT BOOKING MUTATIONS
// ===========================================

/**
 * Create a new hybrid booking
 */
export function useCreateBooking() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (data: CreateBookingInput) => hybridBookingApi.create(data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      queryClient.invalidateQueries({
        queryKey: hybridBookingKeys.availableSlots(
          variables.lessonId,
          variables.weekNumber
        ),
      });
      enqueueSnackbar('Booking confirmed successfully!', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to create booking';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Reschedule a booking
 */
export function useRescheduleBooking() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: RescheduleBookingInput;
    }) => hybridBookingApi.reschedule(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Booking rescheduled successfully!', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to reschedule booking';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Cancel a booking
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) =>
      hybridBookingApi.cancel(id, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Booking cancelled', { variant: 'info' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to cancel booking';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// ADMIN BULK BOOKING MANAGEMENT
// ===========================================

/**
 * Get hybrid booking status for current term
 */
export function useHybridBookingStatus() {
  return useQuery({
    queryKey: [...hybridBookingKeys.all, 'status'] as const,
    queryFn: () => hybridBookingApi.getBookingStatus(),
  });
}

/**
 * Bulk toggle bookings for all hybrid lessons in current term
 */
export function useBulkToggleBookings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (open: boolean) => hybridBookingApi.bulkToggle(open),
    onSuccess: (data, open) => {
      // Invalidate all hybrid booking queries (including parent's my-lessons)
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });

      // Force refetch of parent hybrid lessons to update bookingsOpen status
      queryClient.refetchQueries({
        queryKey: [...hybridBookingKeys.all, 'my-lessons'],
        type: 'active'
      });

      if (data.updatedCount > 0) {
        enqueueSnackbar(
          open
            ? `Bookings opened for ${data.updatedCount} hybrid lesson${data.updatedCount !== 1 ? 's' : ''}. Parents have been notified.`
            : `Bookings closed for ${data.updatedCount} hybrid lesson${data.updatedCount !== 1 ? 's' : ''}.`,
          { variant: open ? 'success' : 'info' }
        );
      } else {
        enqueueSnackbar(
          open ? 'All hybrid bookings are already open.' : 'All hybrid bookings are already closed.',
          { variant: 'info' }
        );
      }
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to toggle bookings';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Bulk toggle OVERFLOW bookings (book a week-N clinic into week N+1) for all
 * hybrid lessons in current term. Opening overflow only applies to lessons
 * whose bookings are already open (enforced server-side).
 */
export function useBulkToggleOverflow() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (open: boolean) => hybridBookingApi.bulkToggleOverflow(open),
    onSuccess: (data, open) => {
      // Invalidate all hybrid booking queries (including parent's my-lessons)
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });

      // Force refetch of parent hybrid lessons to update overflowBookingsOpen status
      queryClient.refetchQueries({
        queryKey: [...hybridBookingKeys.all, 'my-lessons'],
        type: 'active'
      });

      if (data.updatedCount > 0) {
        enqueueSnackbar(
          open
            ? `Overflow bookings opened for ${data.updatedCount} hybrid lesson${data.updatedCount !== 1 ? 's' : ''}.`
            : `Overflow bookings closed for ${data.updatedCount} hybrid lesson${data.updatedCount !== 1 ? 's' : ''}.`,
          { variant: open ? 'success' : 'info' }
        );
      } else {
        enqueueSnackbar(
          open
            ? 'No eligible lessons — overflow needs bookings to be open first.'
            : 'All overflow bookings are already closed.',
          { variant: 'info' }
        );
      }
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to toggle overflow bookings';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// ADMIN HYBRID MANAGEMENT QUERIES
// ===========================================

/**
 * Get all bookings for a lesson (admin/teacher)
 */
export function useLessonBookings(
  lessonId: string,
  filters?: LessonBookingFilters
) {
  return useQuery({
    queryKey: hybridBookingKeys.lessonBookings(lessonId, filters),
    queryFn: () => hybridBookingApi.getLessonBookings(lessonId, filters),
    enabled: !!lessonId,
  });
}

/**
 * Get booking statistics for a lesson
 */
export function useBookingStats(lessonId: string, weekNumber?: number) {
  return useQuery({
    queryKey: hybridBookingKeys.stats(lessonId, weekNumber),
    queryFn: () => hybridBookingApi.getBookingStats(lessonId, weekNumber),
    enabled: !!lessonId,
  });
}

/**
 * Get students who haven't booked for a specific week
 */
export function useUnbookedStudents(lessonId: string, weekNumber: number) {
  return useQuery({
    queryKey: hybridBookingKeys.unbookedStudents(lessonId, weekNumber),
    queryFn: () => hybridBookingApi.getUnbookedStudents(lessonId, weekNumber),
    enabled: !!lessonId && weekNumber > 0,
  });
}

/**
 * Get hybrid individual sessions for the Teacher Tasks page.
 *
 * Returns active HybridBooking rows for GROUP_INDIVIDUAL hybrid lessons whose
 * `scheduledDate` falls in the supplied calendar week. The Teacher Tasks page
 * merges these rows into the lesson list, swapping in for parent rows on
 * individual weeks. Pass `enabled: false` when the page doesn't yet have a
 * week resolved (initial render before user context loads).
 */
export function useHybridIndividualSessionsThisWeek(
  params: TeacherTaskSessionsParams | undefined,
  options?: { enabled?: boolean }
) {
  const enabled = (options?.enabled ?? true) && !!params;
  return useQuery({
    queryKey: hybridBookingKeys.teacherTasks(params ?? 'disabled'),
    queryFn: () => hybridBookingApi.getTeacherTaskSessions(params!),
    enabled,
  });
}

// ===========================================
// ADMIN HYBRID MANAGEMENT MUTATIONS
// ===========================================

/**
 * Open bookings for a lesson
 */
export function useOpenBookings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (lessonId: string) => hybridBookingApi.openBookings(lessonId),
    onSuccess: (_, lessonId) => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.detail(lessonId) });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      enqueueSnackbar('Bookings opened successfully', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to open bookings';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Close bookings for a lesson
 */
export function useCloseBookings() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();

  return useMutation({
    mutationFn: (lessonId: string) => hybridBookingApi.closeBookings(lessonId),
    onSuccess: (_, lessonId) => {
      queryClient.invalidateQueries({ queryKey: lessonKeys.detail(lessonId) });
      queryClient.invalidateQueries({ queryKey: lessonKeys.lists() });
      enqueueSnackbar('Bookings closed', { variant: 'info' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message =
        error.response?.data?.message || 'Failed to close bookings';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Toggle bookings open/close
 */
export function useToggleBookings() {
  const openBookings = useOpenBookings();
  const closeBookings = useCloseBookings();

  return {
    mutate: ({ lessonId, open }: { lessonId: string; open: boolean }) => {
      if (open) {
        openBookings.mutate(lessonId);
      } else {
        closeBookings.mutate(lessonId);
      }
    },
    isPending: openBookings.isPending || closeBookings.isPending,
  };
}

/**
 * One sentence for the admin about what a nudge press did. Exported so the
 * wording is tested rather than eyeballed — a "0 families nudged" that reads
 * as failure, or a count that hides `undelivered`, is a UX defect.
 */
/** Mirrors the server's `MIN_NOTICE_MS` (`findUnbookedClinicWeeks.ts`). */
const NUDGE_MIN_NOTICE_MS = 24 * 60 * 60 * 1000;

/**
 * The clinic weeks the admin nudge chip should be LIVE for. Mirrors the
 * server's refusals in `findUnbookedForClinicWeek` — an open week (no anchor
 * date), a date outside the term, a past or under-24h clinic — so the chip is
 * not rendered where a press can only produce a 409 toast. Andrew, first
 * staging walk 2026-09-04: "it's showing the nudge for weeks past too which
 * seems redundant". The server stays authoritative; this only hides noise.
 */
export function nudgeableClinicWeeks(
  lesson: Pick<TermBookingOpenStatsLesson, 'individualWeeks' | 'openClinicWeeks' | 'dayOfWeek' | 'termStartDate' | 'termEndDate'>,
  now: Date = new Date()
): number[] {
  if (lesson.dayOfWeek == null || !lesson.termStartDate || !lesson.termEndDate) return [];
  const termStart = new Date(lesson.termStartDate);
  const termEnd = new Date(lesson.termEndDate);
  if (Number.isNaN(termStart.getTime()) || Number.isNaN(termEnd.getTime())) return [];
  const open = new Set(lesson.openClinicWeeks ?? []);
  return (lesson.individualWeeks ?? []).filter((wk) => {
    if (open.has(wk)) return false;
    const clinicDate = occurrenceDateForWeek(termStart, wk, lesson.dayOfWeek as number);
    if (clinicDate < termStart || clinicDate > termEnd) return false;
    return clinicDate.getTime() - now.getTime() >= NUDGE_MIN_NOTICE_MS;
  });
}

export function describeNudgeOutcome(data: SendRemindersResult, weekNumber: number): {
  message: string;
  variant: 'success' | 'info' | 'warning';
} {
  const fam = (n: number) => `${n} ${n === 1 ? 'family' : 'families'}`;
  if (data.unbooked === 0) {
    return { message: `Everyone in this class has booked week ${weekNumber}.`, variant: 'info' };
  }
  // A repeat press: nothing sent, everyone already covered. Say that plainly
  // rather than "Nudged 0 families", which reads as a failure.
  if (data.nudged === 0 && data.undelivered === 0 && data.alreadyNudged > 0 && data.alreadyNudged === data.unbooked) {
    return {
      message: `Already nudged — ${fam(data.alreadyNudged)} ${data.alreadyNudged === 1 ? 'was' : 'were'} emailed earlier for week ${weekNumber}.`,
      variant: 'info',
    };
  }
  const parts = [`Nudged ${fam(data.nudged)} to book week ${weekNumber}`];
  if (data.alreadyNudged > 0) parts.push(`${fam(data.alreadyNudged)} already nudged`);
  if (data.noContactEmail > 0) parts.push(`${fam(data.noContactEmail)} with no email on file`);
  if (data.undelivered > 0) parts.push(`${fam(data.undelivered)} could NOT be emailed`);
  const variant = data.undelivered > 0 ? 'warning' : data.nudged > 0 ? 'success' : 'info';
  return { message: parts.join(' · ') + '.', variant };
}

/** Did the request time out or hit a proxy timeout, i.e. is the server still working? */
function isStillRunning(error: { code?: string; response?: { status?: number } }): boolean {
  // ECONNABORTED is what axios emits on a client timeout while its transitional
  // `clarifyTimeoutError` default is false; ETIMEDOUT is what it becomes if that
  // default ever flips. 502/504 are nginx giving up on a long upstream request.
  return (
    error.code === 'ECONNABORTED' ||
    error.code === 'ETIMEDOUT' ||
    error.response?.status === 504 ||
    error.response?.status === 502
  );
}

/**
 * Admin "nudge now" for one class + clinic week (Sprint B).
 *
 * ⚠️ NEVER auto-retries. The server sends inline and serially; under a Mailgun
 * rate limit the request can outlive nginx's timeout while the server finishes
 * and the ledger records every send. On a timeout the admin is told to re-check
 * the list, not to press again — a re-press is safe (it reports alreadyNudged)
 * but it would look like a second failure.
 */
export function useSendReminders() {
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    retry: false,
    mutationFn: ({ lessonId, weekNumber }: { lessonId: string; weekNumber: number }) =>
      hybridBookingApi.sendReminders(lessonId, weekNumber),
    onSuccess: (data, { weekNumber }) => {
      const { message, variant } = describeNudgeOutcome(data, weekNumber);
      enqueueSnackbar(message, { variant });
      void queryClient.invalidateQueries({ queryKey: ['hybridBooking', 'termBookingOpenStats'] });
      void queryClient.invalidateQueries({ queryKey: ['hybridBooking', 'bookingOpenStats'] });
    },
    onError: (error: Error & { code?: string; response?: { status?: number; data?: { message?: string } } }) => {
      if (isStillRunning(error)) {
        enqueueSnackbar(
          'The nudge is still running on the server — check the list again in a minute rather than pressing again.',
          { variant: 'info', autoHideDuration: 10000 }
        );
        return;
      }
      const message = error.response?.data?.message || 'Failed to send reminders';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

// ===========================================
// CALENDAR QUERIES
// ===========================================

/**
 * Get calendar events (admin/teacher) with pagination
 * @param filters - Optional filters including pagination
 */
export function useCalendarEventsPaginated(filters?: CalendarEventsFilters) {
  return useQuery({
    queryKey: calendarKeys.events(filters),
    queryFn: () => calendarApi.getEvents(filters),
  });
}

/**
 * Get all calendar events (admin/teacher) - fetches all pages
 * Use for calendar views that need all events rendered at once
 * @param filters - Optional filters (excluding pagination)
 */
export function useCalendarEvents(filters?: Omit<CalendarEventsFilters, 'page' | 'limit'>) {
  return useQuery({
    queryKey: [...calendarKeys.events(filters), 'all'],
    queryFn: () => calendarApi.getAllEvents(filters),
  });
}

/**
 * Get calendar events for parent's children with pagination
 */
export function useMyCalendarEventsPaginated(filters?: CalendarEventsFilters) {
  return useQuery({
    queryKey: calendarKeys.myEvents(filters),
    queryFn: () => calendarApi.getMyEvents(filters),
  });
}

/**
 * Get all calendar events for parent's children - fetches all pages
 * Use for calendar views that need all events rendered at once
 */
export function useMyCalendarEvents(filters?: Omit<CalendarEventsFilters, 'page' | 'limit'>) {
  return useQuery({
    queryKey: [...calendarKeys.myEvents(filters), 'all'],
    queryFn: () => calendarApi.getAllMyEvents(filters),
  });
}

/**
 * Terms for the school - every role may read them, students included (#272). Pages that may also
 * read the admin list go through `useViewTerms`, which picks one source.
 */
export function useCalendarTerms(options: { enabled?: boolean } = {}) {
  return useQuery<TermOption[]>({
    queryKey: ['calendar', 'terms'],
    queryFn: () => calendarApi.getTerms(),
    enabled: options.enabled ?? true,
  });
}

/**
 * Get instruments - accessible to parents, teachers and admins
 */
export function useCalendarInstruments(options: { enabled?: boolean } = {}) {
  return useQuery<InstrumentOption[]>({
    queryKey: ['calendar', 'instruments'],
    queryFn: () => calendarApi.getInstruments(),
    enabled: options.enabled ?? true,
  });
}

/**
 * Get program levels - accessible to parents, teachers and admins
 */
export function useCalendarProgramLevels(options: { enabled?: boolean } = {}) {
  return useQuery<ProgramLevelOption[]>({
    queryKey: ['calendar', 'program-levels'],
    queryFn: () => calendarApi.getProgramLevels(),
    enabled: options.enabled ?? true,
  });
}

// ===========================================
// ADMIN PRE-BOOKING HOOKS
// ===========================================

export function useAdminAvailableSlots(
  lessonId: string,
  weekNumber: number,
  bookingId?: string,
  teacherId?: string
) {
  return useQuery({
    queryKey: hybridBookingKeys.adminAvailableSlots(lessonId, weekNumber, bookingId, teacherId),
    queryFn: () =>
      hybridBookingApi.getAdminAvailableSlots(lessonId, weekNumber, bookingId, teacherId),
    enabled: !!lessonId && weekNumber > 0,
  });
}

export function useEligibleClinicTeachers(lessonId: string) {
  return useQuery({
    queryKey: hybridBookingKeys.eligibleClinicTeachers(lessonId),
    queryFn: () => hybridBookingApi.getEligibleClinicTeachers(lessonId),
    enabled: !!lessonId,
  });
}

export function useAdminPreBookings(filters?: AdminPreBookingFilters) {
  return useQuery({
    queryKey: hybridBookingKeys.adminPreBookings(filters),
    queryFn: () => hybridBookingApi.getAdminPreBookings(filters),
    enabled: !!filters?.lessonId,
  });
}

export function useCreateAdminPreBooking() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (data: AdminPreBookingInput) => hybridBookingApi.createAdminPreBooking(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Slot pre-booked successfully!', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to create pre-booking';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

export function useCancelAdminPreBooking() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: (input: string | { id: string; notifyParents?: boolean }) =>
      typeof input === 'string'
        ? hybridBookingApi.cancelAdminPreBooking(input)
        : hybridBookingApi.cancelAdminPreBooking(input.id, input.notifyParents),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Pre-booking cancelled', { variant: 'info' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to cancel pre-booking';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Admin: get all hybrid bookings for a student
 */
export function useAdminStudentBookings(
  studentId: string | null | undefined,
  options?: { includeCancelled?: boolean; enabled?: boolean }
) {
  const includeCancelled = options?.includeCancelled ?? false;
  return useQuery({
    queryKey: hybridBookingKeys.adminStudentBookings(studentId || '', includeCancelled),
    queryFn: () =>
      hybridBookingApi.getAdminStudentBookings(studentId as string, { includeCancelled }),
    enabled: !!studentId && (options?.enabled ?? true),
  });
}

/**
 * Admin: reschedule any hybrid booking (bypasses parent ownership, bookingsOpen, 24h notice)
 */
export function useAdminRescheduleBooking() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: RescheduleBookingInput }) =>
      hybridBookingApi.adminRescheduleBooking(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Booking rescheduled', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to reschedule booking';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Admin: open/close ONE clinic week of a class to any term week
 * (HybridLessonPattern.openClinicWeeks). The class detail page is the ONLY
 * admin door by design. While open, that clinic's sessions — booked or not
 * yet booked — can be placed on any day/time the teacher is available.
 */
export function useSetClinicWeekOpen() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({
      lessonId,
      clinicWeek,
      open,
    }: {
      lessonId: string;
      clinicWeek: number;
      open: boolean;
    }) => hybridBookingApi.setClinicWeekOpen(lessonId, clinicWeek, open),
    onSuccess: (_result, variables) => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      // The lesson detail (pattern) carries openClinicWeeks — refresh it too.
      queryClient.invalidateQueries({ queryKey: ['lessons'] });
      enqueueSnackbar(
        variables.open
          ? `Week ${variables.clinicWeek} clinic opened — families can book any week the teacher is available`
          : `Week ${variables.clinicWeek} clinic closed to its scheduled week`,
        { variant: 'success' }
      );
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to update clinic week';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Admin: override the room of a single 1:1 clinic booking ("Change Location").
 */
export function useAdminChangeBookingRoom() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ChangeBookingRoomInput }) =>
      hybridBookingApi.adminChangeBookingRoom(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Clinic location changed', { variant: 'success' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to change location';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Admin: set/change/clear the teacher of a single 1:1 clinic booking ("Change Teacher").
 * A 409 (teacher unavailable / conflict) is left to the caller — the dialog shows the
 * reason inline and offers "Save anyway" — so no error snackbar for that status.
 */
export function useAdminChangeBookingTeacher() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: ChangeBookingTeacherInput }) =>
      hybridBookingApi.adminChangeBookingTeacher(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Clinic teacher updated', { variant: 'success' });
    },
    onError: (error: Error & { response?: { status?: number; data?: { message?: string } } }) => {
      if (error.response?.status === 409) return;
      const message = error.response?.data?.message || 'Failed to change teacher';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}

/**
 * Get booking-open stats for a single lesson
 */
export function useBookingOpenStats(lessonId: string | null) {
  return useQuery<BookingOpenStats>({
    queryKey: ['hybridBooking', 'bookingOpenStats', lessonId],
    queryFn: () => hybridBookingApi.getBookingOpenStats(lessonId!),
    enabled: !!lessonId,
    staleTime: 30_000,
  });
}

/**
 * Get aggregated booking-open stats across all open hybrid lessons in the current term
 */
export function useTermBookingOpenStats(enabled: boolean) {
  return useQuery<TermBookingOpenStats>({
    queryKey: ['hybridBooking', 'termBookingOpenStats'],
    queryFn: () => hybridBookingApi.getTermBookingOpenStats(),
    enabled,
    staleTime: 30_000,
  });
}

/**
 * Admin: cancel any hybrid booking (bypasses parent ownership)
 */
export function useAdminCancelBooking() {
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  return useMutation({
    mutationFn: ({
      id,
      reason,
      notifyParents,
    }: {
      id: string;
      reason?: string;
      notifyParents?: boolean;
    }) => hybridBookingApi.adminCancelBooking(id, reason, notifyParents),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: hybridBookingKeys.all });
      queryClient.invalidateQueries({ queryKey: calendarKeys.all });
      enqueueSnackbar('Booking cancelled', { variant: 'info' });
    },
    onError: (error: Error & { response?: { data?: { message?: string } } }) => {
      const message = error.response?.data?.message || 'Failed to cancel booking';
      enqueueSnackbar(message, { variant: 'error' });
    },
  });
}
